/**
 * Social Composer — turn a generated design into a platform-sized social-media
 * GRAPHIC (background + the design + editable, brand-correct text), exported at
 * exact platform pixels. This is the piece the old share panel lacked: it only
 * cover-cropped the raw image; here the design is composited into a real post.
 *
 * Why a canvas (not AI-baked text): readable, editable, brand-correct text — and
 * Chinese — is unreliable when baked into a diffusion image (Ideogram's own docs
 * flag non-Latin text). So text/logo are an editable canvas OVERLAY layer; only
 * the optional scene BACKGROUND is AI-generated (Create/fal). why → seede.ai
 * research 2026-06-26 (generated-image and text-layout kept as separate layers
 * across every commerce tool surveyed).
 *
 * Phase-1 (IG/global first): platform sizes + background modes (brand fill /
 * design fill) + headline + subtext + position presets + PNG export. AI scene
 * background, logo + brand-kit are layered on top. H-177 (bao 2026-10-16):
 * the /api/share caption is cut for good (D3a — backend decommissioned per
 * H-079) and the H-164 platforms-404 guard is removed (D4 re-scope) — the
 * composer is 100% client-side, reachable from the create result Share entry.
 * A copy-share-link action carries the H-176 credit token when available.
 *
 * This file owns the composer's mutable state (_root/_state) + modal UI +
 * events; static config lives in social-composer-config.js, canvas rendering
 * in social-composer-canvas.js, styles in social-composer-styles.js.
 */

import { loadImage } from '../../utils/mockup-composite.js'
import { showToast } from '../../utils/toast.js'
import { generateFromTemplate } from '../../api/prompts.js'
import { fetchShareToken, buildShareUrl } from '../../api/share-token.js'
import {
  PLATFORMS, BG_MODES, AI_PRESETS, POSITIONS, LOGO_POS,
  BRAND_SWATCH_VARS, LAYOUTS, FONTS, brandColor, escAttr, toHex,
} from './social-composer-config.js'
import { drawComposition } from './social-composer-canvas.js'
import { injectStyles } from './social-composer-styles.js'

let _root = null
let _state = null

/**
 * Open the composer for a generated result image.
 *
 * H-177 (D4 re-scope, bao 2026-10-16): the H-164 platforms-404 guard is
 * removed — the composer no longer touches the decommissioned /api/share/*
 * backend at all, so it renders unconditionally. imageData.shareUrl (when the
 * caller has a canonical PDP link) becomes the copy-share-link target; the
 * raw image URL is the pre-publish fallback (D5: PDP preferred when it
 * exists).
 *
 * @param {{ url: string, id?: string, shareUrl?: string }} imageData
 */
export async function openSocialComposer(imageData) {
  if (!imageData?.url) { showToast('No image to compose', 'warning'); return null }
  if (_root) closeComposer()

  _state = {
    image: null,
    imageUrl: imageData.url,
    shareUrl: imageData.shareUrl || null,
    id: imageData.id ?? null,
    platform: PLATFORMS[0],
    bgMode: 'brand',
    headline: 'Your headline',
    subtext: '',
    font: FONTS[0],
    color: brandColor('--brand-primary-fg', '#ffffff'),
    bg: brandColor('--brand-primary', '#1f1f1f'),
    position: 'bottom',
    eyebrow: '',
    logo: 'off',       // off | top | bottom — stamps the mamamiya wordmark
    aiBg: null,        // loaded HTMLImageElement of the generated scene
    aiPrompt: AI_PRESETS[0].prompt,
    aiLoading: false,
  }

  injectStyles()
  renderShell()

  try {
    _state.image = await loadImage(imageData.url)
  } catch {
    showToast('Could not load the design image', 'error')
  }
  draw()
}

function renderShell() {
  _root = document.createElement('div')
  _root.className = 'sc-overlay'
  _root.innerHTML = `
    <div class="sc-modal" role="dialog" aria-label="Make a social post">
      <div class="sc-head">
        <h3>Make a social post</h3>
        <button class="sc-x" id="scClose" aria-label="Close"><i class="ph ph-x"></i></button>
      </div>
      <div class="sc-body">
        <div class="sc-stage"><canvas id="scCanvas"></canvas></div>
        <div class="sc-controls" id="scControls">${controlsHTML()}</div>
      </div>
    </div>`
  document.body.appendChild(_root)
  // Static handlers (survive control re-renders): close + backdrop + ESC.
  _root.querySelector('#scClose').addEventListener('click', closeComposer)
  _root.addEventListener('click', e => { if (e.target === _root) closeComposer() })
  document.addEventListener('keydown', onKey)
  bindControls()
}

function controlsHTML() {
  const swatches = BRAND_SWATCH_VARS
    .map(v => ({ v, c: brandColor(v, '') })).filter(s => s.c)
    .map(s => `<button class="sc-sw" data-c="${escAttr(s.c)}" style="background:${s.c}" aria-label="brand colour"></button>`).join('')
  return `
    <label class="sc-grp"><span>Quick layout</span>
      <div class="sc-seg sc-wrap" id="scLayout">
        ${LAYOUTS.map(l => `<button data-name="${escAttr(l.name)}">${l.name}</button>`).join('')}
      </div>
    </label>
    <label class="sc-grp"><span>Format</span>
      <div class="sc-seg" id="scPlatform">
        ${PLATFORMS.map(p => `<button data-id="${p.id}" class="${p.id === _state.platform.id ? 'on' : ''}">${p.name}</button>`).join('')}
      </div>
    </label>
    <label class="sc-grp"><span>Background</span>
      <div class="sc-seg" id="scBg">
        ${BG_MODES.map(m => `<button data-id="${m.id}" class="${m.id === _state.bgMode ? 'on' : ''}">${m.name}</button>`).join('')}
      </div>
    </label>
    <div class="sc-ai" id="scAi" style="display:${_state.bgMode === 'ai' ? '' : 'none'}">
      <div class="sc-ai-presets" id="scAiPresets">
        ${AI_PRESETS.map((p, i) => `<button data-i="${i}" class="${p.prompt === _state.aiPrompt ? 'on' : ''}">${p.name}</button>`).join('')}
      </div>
      <input type="text" id="scAiPrompt" maxlength="200" value="${escAttr(_state.aiPrompt)}" placeholder="Describe a background scene" />
      <button class="sc-ai-gen" id="scAiGen"><i class="ph ph-sparkle"></i> Generate background</button>
    </div>
    <label class="sc-grp"><span>Eyebrow / tag</span>
      <input type="text" id="scEyebrow" maxlength="40" value="${escAttr(_state.eyebrow)}" placeholder="e.g. NEW DROP" />
    </label>
    <label class="sc-grp"><span>Headline</span>
      <input type="text" id="scHeadline" maxlength="80" value="${escAttr(_state.headline)}" placeholder="Your headline" />
    </label>
    <label class="sc-grp"><span>Subtext</span>
      <input type="text" id="scSub" maxlength="120" value="${escAttr(_state.subtext)}" placeholder="Optional second line" />
    </label>
    <label class="sc-grp"><span>Font</span>
      <select id="scFont">${FONTS.map(f => `<option value="${escAttr(f)}" ${f === _state.font ? 'selected' : ''}>${f}</option>`).join('')}</select>
    </label>
    <label class="sc-grp"><span>Text position</span>
      <div class="sc-seg" id="scPos">
        ${POSITIONS.map(p => `<button data-id="${p.id}" class="${p.id === _state.position ? 'on' : ''}">${p.name}</button>`).join('')}
      </div>
    </label>
    <label class="sc-grp"><span>Logo</span>
      <div class="sc-seg" id="scLogo">
        ${LOGO_POS.map(p => `<button data-id="${p.id}" class="${p.id === _state.logo ? 'on' : ''}">${p.name}</button>`).join('')}
      </div>
    </label>
    <label class="sc-grp"><span>Brand colours</span>
      <div class="sc-swatches" id="scSwatches">${swatches}</div>
    </label>
    <div class="sc-row">
      <label class="sc-mini"><span>Text</span><input type="color" id="scColor" value="${toHex(_state.color)}" /></label>
      <label class="sc-mini"><span>BG</span><input type="color" id="scBgColor" value="${toHex(_state.bg)}" /></label>
    </div>
    <button class="sc-share" id="scShare"><i class="ph ph-link"></i> Copy share link</button>
    <button class="sc-export" id="scExport"><i class="ph ph-download-simple"></i> Download PNG</button>`
}

function bindControls() {
  segBind('#scPlatform', id => { _state.platform = PLATFORMS.find(p => p.id === id); draw() })
  segBind('#scBg', id => { _state.bgMode = id; toggleAi(); draw() })
  segBind('#scPos', id => { _state.position = id; draw() })
  segBind('#scLogo', id => { _state.logo = id; draw() })

  _root.querySelector('#scLayout').addEventListener('click', e => {
    const btn = e.target.closest('button'); if (!btn) return
    applyLayout(btn.dataset.name)
  })

  // AI background sub-panel.
  const presets = _root.querySelector('#scAiPresets')
  presets.addEventListener('click', e => {
    const btn = e.target.closest('button'); if (!btn) return
    presets.querySelectorAll('button').forEach(b => b.classList.remove('on'))
    btn.classList.add('on')
    _state.aiPrompt = AI_PRESETS[Number(btn.dataset.i)].prompt
    _root.querySelector('#scAiPrompt').value = _state.aiPrompt
  })
  _root.querySelector('#scAiPrompt').addEventListener('input', e => { _state.aiPrompt = e.target.value })
  _root.querySelector('#scAiGen').addEventListener('click', generateAiBackground)

  _root.querySelector('#scSwatches').addEventListener('click', e => {
    const sw = e.target.closest('.sc-sw'); if (!sw) return
    _state.bg = sw.dataset.c
    const picker = _root.querySelector('#scBgColor'); if (picker) picker.value = toHex(_state.bg)
    if (_state.bgMode === 'ai') { _state.bgMode = 'brand'; syncSeg('#scBg', 'brand'); toggleAi() }
    draw()
  })

  _root.querySelector('#scEyebrow').addEventListener('input', e => { _state.eyebrow = e.target.value; draw() })
  _root.querySelector('#scHeadline').addEventListener('input', e => { _state.headline = e.target.value; draw() })
  _root.querySelector('#scSub').addEventListener('input', e => { _state.subtext = e.target.value; draw() })
  _root.querySelector('#scFont').addEventListener('change', e => { _state.font = e.target.value; draw() })
  _root.querySelector('#scColor').addEventListener('input', e => { _state.color = e.target.value; draw() })
  _root.querySelector('#scBgColor').addEventListener('input', e => { _state.bg = e.target.value; draw() })
  _root.querySelector('#scShare').addEventListener('click', copyShareLink)
  _root.querySelector('#scExport').addEventListener('click', exportPng)
}

/** Copy the share link for this post: the caller's canonical shareUrl (PDP
 *  when one exists, D5) or the raw image URL pre-publish, carrying the H-176
 *  credit token when the (signed-in) token endpoint answers; anonymous or a
 *  pending endpoint degrades to the bare link — sharing must never block. */
async function copyShareLink() {
  const base = _state.shareUrl || _state.imageUrl
  let link = base
  try {
    const share = await fetchShareToken()
    link = share?.url || buildShareUrl(base, share?.token)
  } catch { /* token endpoint pending or signed out — bare link still shares */ }
  try {
    await navigator.clipboard.writeText(link)
    showToast('Share link copied', 'success', 1500)
  } catch {
    showToast('Copy failed', 'error')
  }
}

/** Apply a one-tap layout bundle, then re-render + re-bind the controls so every
 *  control reflects the new state (the canvas redraws from state). */
function applyLayout(name) {
  const layout = LAYOUTS.find(l => l.name === name)
  if (!layout) return
  Object.assign(_state, layout.s)
  _root.querySelector('#scControls').innerHTML = controlsHTML()
  bindControls()
  draw()
}

function syncSeg(sel, id) {
  const seg = _root.querySelector(sel); if (!seg) return
  seg.querySelectorAll('button').forEach(b => b.classList.toggle('on', b.dataset.id === id))
}

function segBind(sel, cb) {
  const seg = _root.querySelector(sel)
  seg.addEventListener('click', e => {
    const btn = e.target.closest('button'); if (!btn) return
    seg.querySelectorAll('button').forEach(b => b.classList.remove('on'))
    btn.classList.add('on')
    cb(btn.dataset.id)
  })
}

function onKey(e) { if (e.key === 'Escape') closeComposer() }

function toggleAi() {
  const el = _root?.querySelector('#scAi')
  if (el) el.style.display = _state.bgMode === 'ai' ? '' : 'none'
}

/** Generate a decorative scene background (text-to-image, no design baked in),
 *  then composite the design + text on top. why → seede-style separate layers. */
async function generateAiBackground() {
  if (_state.aiLoading) return
  const btn = _root?.querySelector('#scAiGen'); if (!btn) return
  _state.aiLoading = true
  btn.disabled = true
  const label = btn.innerHTML
  btn.innerHTML = '<i class="ph ph-circle-notch sc-spin"></i> Generating…'
  try {
    const res = await generateFromTemplate({
      intent: 'ideogram',
      prompt: _state.aiPrompt,
      aspect_ratio: _state.platform.ar,
    })
    const url = res?.data?.asset?.public_url
    if (!url) throw new Error('no image returned')
    _state.aiBg = await loadImage(url)
    draw()
    showToast('Background ready', 'success', 1500)
  } catch (err) {
    showToast(`Background failed: ${err.message || 'try again'}`, 'error')
  } finally {
    _state.aiLoading = false
    btn.disabled = false
    btn.innerHTML = label
  }
}

/** Redraw the preview canvas from current state (rendering lives in
 *  social-composer-canvas.js; shared by preview + export). */
function draw() {
  const canvas = _root?.querySelector('#scCanvas'); if (!canvas) return
  drawComposition(canvas, _state)
}

async function exportPng() {
  const canvas = _root?.querySelector('#scCanvas'); if (!canvas) return
  // Ensure the brand font is ready so the exported text isn't a fallback face.
  try { await document.fonts.load(`700 80px "${_state.font}"`) ; draw() } catch {}
  canvas.toBlob(blob => {
    if (!blob) { showToast('Export failed (image may be cross-origin)', 'error'); return }
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `mamamiya_${_state.platform.id}_${_state.platform.w}x${_state.platform.h}.png`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(a.href)
    showToast('Downloaded', 'success')
  }, 'image/png')
}

function closeComposer() {
  document.removeEventListener('keydown', onKey)
  _root?.remove(); _root = null; _state = null
}
